/**
 * Salovare brand tokens.
 *
 * Every brand colour is exposed as a CSS custom property so a store's custom
 * palette (see BrandThemeProvider) can re-colour the whole app at runtime.
 * Use BRAND / brandAlpha in styles instead of hex literals.
 */

export const BRAND_COLOR_ROLES = ['primary', 'accent', 'text', 'background'];

export const DEFAULT_BRAND_COLORS = Object.freeze({
  primary: '#22344A',
  accent: '#A15C43',
  text: '#2C2A26',
  background: '#F5F2EE',
});

export const BRAND_FONT_FAMILY =
  '"Neue Montreal", "PP Neue Montreal", "Hanken Grotesk", "Helvetica Neue", Arial, sans-serif';

export const BRAND_RADIUS = Object.freeze({
  button: '10px',
  card: '16px',
  pill: '999px',
});

/** Palette keys that become CSS variables (and `-rgb` channel variables). */
export const BRAND_VARIABLE_KEYS = [
  'primary',
  'primaryDark',
  'primaryLight',
  'primaryText',
  'onPrimary',
  'accent',
  'accentDark',
  'accentLight',
  'accentText',
  'onAccent',
  'text',
  'background',
  'surface',
];

const toKebab = (key) => key.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`);

export const brandVariableName = (key) => `--sv-${toKebab(key)}`;

/** A brand colour at the given opacity, e.g. brandAlpha('accent', 0.12). */
export const brandAlpha = (key, opacity) => `rgb(var(${brandVariableName(key)}-rgb) / ${opacity})`;

const brandVar = (key) => `var(${brandVariableName(key)})`;

export const BRAND = Object.freeze({
  primary: brandVar('primary'),
  primaryDark: brandVar('primaryDark'),
  primaryLight: brandVar('primaryLight'),
  /** Primary used as text/icon colour on light surfaces (contrast-safe). */
  primaryText: brandVar('primaryText'),
  onPrimary: brandVar('onPrimary'),
  onPrimaryMuted: brandAlpha('onPrimary', 0.72),
  onPrimaryLine: brandAlpha('onPrimary', 0.16),
  accent: brandVar('accent'),
  accentDark: brandVar('accentDark'),
  /** Accent used as text/icon colour on primary surfaces (contrast-safe). */
  accentLight: brandVar('accentLight'),
  /** Accent used as text/icon colour on light surfaces (contrast-safe). */
  accentText: brandVar('accentText'),
  onAccent: brandVar('onAccent'),
  text: brandVar('text'),
  textMuted: brandAlpha('text', 0.72),
  textSubtle: brandAlpha('text', 0.5),
  background: brandVar('background'),
  surface: brandVar('surface'),
  line: brandAlpha('text', 0.14),
  lineSoft: brandAlpha('text', 0.08),
});
